import { useCallback, useEffect, useState } from "react";

const CART_KEY = "kelifa_cart";

const isEmptyValue = (value) => (
  value === undefined
  || value === null
  || value === ""
  || (Array.isArray(value) && value.length === 0)
);

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    const normalized = value.map(normalizeValue).filter((entry) => !isEmptyValue(entry));
    return normalized.every((entry) => typeof entry !== "object")
      ? normalized.sort((a, b) => String(a).localeCompare(String(b), "es"))
      : normalized;
  }

  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const normalized = normalizeValue(value[key]);
        if (!isEmptyValue(normalized)) acc[key] = normalized;
        return acc;
      }, {});
  }

  if (typeof value === "string") return value.trim();
  return value;
};

const itemSignature = (item) => JSON.stringify({
  product_id: item.product_id,
  base_price: Number(item.base_price || 0),
  requires_quote: !!item.requires_quote,
  customization: normalizeValue(item.customization || {}),
});

const normalizeCartItem = (item) => {
  const normalized = {
    cart_id: item.cart_id || `${item.product_id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    product_id: item.product_id,
    product_name: item.product_name,
    image: item.image || "",
    quantity: Math.max(1, Number(item.quantity || 1)),
    base_price: Number(item.base_price || 0),
    requires_quote: !!item.requires_quote,
    customization: normalizeValue(item.customization || {}),
  };
  return { ...normalized, signature: itemSignature(normalized) };
};

const mergeCartItems = (items) => {
  const grouped = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const normalized = normalizeCartItem(item);
    const existing = grouped.get(normalized.signature);
    if (existing) {
      grouped.set(normalized.signature, {
        ...existing,
        quantity: Number(existing.quantity || 1) + Number(normalized.quantity || 1),
      });
      return;
    }
    grouped.set(normalized.signature, normalized);
  });

  return Array.from(grouped.values());
};

const readCart = () => {
  try {
    return mergeCartItems(JSON.parse(localStorage.getItem(CART_KEY) || "[]"));
  } catch {
    return [];
  }
};

const writeCart = (items) => {
  const next = mergeCartItems(items);
  localStorage.setItem(CART_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("kelifa-cart-updated", { detail: next }));
  return next;
};

export const useCart = () => {
  const [items, setItems] = useState(readCart);

  useEffect(() => {
    const sync = () => setItems(readCart());
    window.addEventListener("storage", sync);
    window.addEventListener("kelifa-cart-updated", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("kelifa-cart-updated", sync);
    };
  }, []);

  const addItem = useCallback((item) => {
    const current = readCart();
    const candidate = normalizeCartItem(item);
    const existing = current.find((cartItem) => cartItem.signature === candidate.signature);
    const next = existing
      ? current.map((cartItem) => (
        cartItem.signature === candidate.signature
          ? { ...cartItem, quantity: Number(cartItem.quantity || 1) + Number(candidate.quantity || 1) }
          : cartItem
      ))
      : [...current, candidate];
    setItems(writeCart(next));
  }, []);

  const removeItem = useCallback((cartId) => {
    const next = readCart().filter((item) => item.cart_id !== cartId);
    setItems(writeCart(next));
  }, []);

  const updateQuantity = useCallback((cartId, quantity) => {
    const next = readCart().map((item) => (
      item.cart_id === cartId
        ? { ...item, quantity: Math.max(1, Number(quantity || 1)) }
        : item
    ));
    setItems(writeCart(next));
  }, []);

  const clearCart = useCallback(() => {
    writeCart([]);
    setItems([]);
  }, []);

  const subtotal = items.reduce((sum, item) => sum + (item.requires_quote ? 0 : Number(item.base_price || 0) * Number(item.quantity || 1)), 0);
  const count = items.reduce((sum, item) => sum + Number(item.quantity || 1), 0);

  return { items, addItem, removeItem, updateQuantity, clearCart, subtotal, count };
};
